import React, { useEffect, useMemo, useState } from 'react';
import AssistantBubble from './AssistantBubble';
import { NavLink, useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useBranding } from '../contexts/BrandingContext';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from './ui/sheet';
import AuroraSky from './AuroraSky';
import { cn } from '@/lib/utils';
import { PRODUCT_NAME, PRODUCT_VERSION, BRAND_LOGO } from '../lib/version';
import {
  LayoutDashboard, Layers, Smartphone, Activity, ScrollText, Settings, LogOut, Menu,
  MessageSquare, Repeat, MonitorPlay, Server, Users, Bug, Skull, LifeBuoy, Flag, History,
  MoreHorizontal, ChevronRight, ShoppingCart, SlidersHorizontal, Boxes, UserCheck, CalendarClock, ScanSearch, Inbox, KeyRound
} from 'lucide-react';
import { r16mUiEnabled, r16o3UiEnabled } from '../lib/r14Switch';
import useChatsSummary from '../hooks/useChatsSummary';
import { navPillText } from '../lib/chatsView';
import useDoctorMe from '../hooks/useDoctorMe';
import { AccountBanner, CreditsChip } from './doctor/AccountBanner';
import { doctorUiOn } from '../lib/doctorSwitch';

/*
 * The V3 app shell (2026-08-23). Three layouts from one component, mobile-first:
 *   < lg  : top bar (brand + menu) + a BOTTOM TAB BAR with the five places people actually go,
 *           "More" opens a sheet with everything else. Safe-area aware (home indicator).
 *   ≥ lg  : a fixed left sidebar with grouped navigation, the top bar becomes the page title row.
 * Admin links appear only for ROLE_ADMIN (the /me "owner" vocabulary); Flags only for the super
 * admin. Content never scrolls sideways: the main column is min-w-0 and pages wrap dense tables
 * in <TableScroll>.
 */

const FULL_WIDTH_PAGES = ['/admin/ui-debug'];

const GROUPS = [
  { label: 'Overview', items: [
    { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/monitor', label: 'Monitor', icon: MonitorPlay },
    // r16p: the DM inbox of every slot (pages/InboxPage.js; switch r16pUiEnabled). Drawn only once GET /uiapi/chats/summary answers
    // (a server before r16p: no item), with the count of conversations with unread as its pill. O13: no bottom-bar swap.
    { path: '/inbox', label: 'Inbox', icon: Inbox, r16p: true, pill: 'chats' },
  ] },
  { label: 'Fleet', items: [
    { path: '/slots', label: 'Slots', icon: Layers },
    { path: '/devices', label: 'Devices', icon: Smartphone },
    // r16: accounts (with encrypted logins), phones, profiles, network, clients and their history.
    { path: '/inventory', label: 'Inventory', icon: Boxes },
    { path: '/sessions', label: 'Sessions', icon: Activity, soon: true },
    { path: '/logs', label: 'Logs', icon: ScrollText, soon: true },
  ] },
  // SMM is its own domain (panel orders + the panel connection), not a "Growth" tactic like
  // campaigns or reposting. Its own heading also gives the pages still to be moved off the legacy
  // Thymeleaf UI - subscription orders, boosting/test orders - somewhere obvious to land.
  // `requires` is an ENTITLEMENT, not a preference: the provider grants it, the customer cannot.
  // V1 does exactly this - bot-settings.html:249 wraps the SMM Orders link in
  // th:hidden="${!enableSmmFeature}", and boosting-order.html hides its whole SMM block (lines
  // 124-350) the same way. V2 dropped both gates and V3 inherited that, so every account saw an
  // SMM menu it had not been sold.
  { label: 'SMM', requires: 'enableSmmFeature', items: [
    { path: '/smm-orders', label: 'Orders', icon: ShoppingCart },
    // r16o3: V1 /smmSubscriptionOrders/ - subscription orders with pause / resume (pages/SmmSubscriptionsPage.js; switch r16o3UiEnabled)
    { path: '/smm-subscriptions', label: 'Subscriptions', icon: CalendarClock, r16o3: true },
    { path: '/smm-settings', label: 'Settings', icon: SlidersHorizontal },
  ] },
  { label: 'Growth', items: [
    // r16m: follow-back by source type and target, per batch or slot range (pages/FollowBackPage.js; switch r16mUiEnabled)
    { path: '/follow-back', label: 'Follow-back', icon: UserCheck, r16m: true },
    // r16o3: the inbuilt-scraping block of V1 /orderManagement/accountSettings - every account sees it, as in V1 (pages/InbuiltScrapingPage.js)
    { path: '/inbuilt-scraping', label: 'Inbuilt scraping', icon: ScanSearch, r16o3: true },
    { path: '/campaigns', label: 'Campaigns', icon: MessageSquare, soon: true },
    { path: '/reposting', label: 'Reposting', icon: Repeat, soon: true },
  ] },
  { label: 'Help', items: [
    // The Undead Assistant is NOT listed here any more - it is a support bubble pinned to the
    // bottom-right of every page (AssistantBubble, rendered below). A question about a setting
    // arrives while you are looking at that setting, and a nav entry meant leaving the page to
    // ask it. The route still exists and the bubble's expand button hands over to it, so a
    // bookmark or a deep link keeps working.
    { path: '/versions', label: 'Versions', icon: History },
    // r16p: API keys for the public Chats API, "Lock API access" and the docs (pages/ApiSettingsPage.js)
    { path: '/settings/api', label: 'API access', icon: KeyRound, r16p: true },
    { path: '/settings', label: 'Settings', icon: Settings },
  ] },
];

const ADMIN_ITEMS = [
  { path: '/admin/users', label: 'Users', icon: Users },
  { path: '/admin/support', label: 'Support', icon: LifeBuoy },
  { path: '/admin/ui-debug', label: 'UI Debug', icon: Bug },
  { path: '/admin/feature-flags', label: 'Flags', icon: Flag, superAdmin: true },
  { path: '/settings/system', label: 'System', icon: Server },
];

const BOTTOM_TABS = ['/dashboard', '/slots', '/monitor', '/devices'];

const TITLES = [
  ['/dashboard', 'Dashboard'], ['/monitor', 'Monitor'], ['/versions', 'Versions'], ['/inbox', 'Inbox'],
  ['/slots/', 'Slot'], ['/slots', 'Slots'], ['/devices/', 'Device'], ['/devices', 'Devices'], ['/inventory', 'Inventory'],
  ['/sessions', 'Sessions'], ['/logs', 'Logs'], ['/follow-back', 'Follow-back'], ['/smm-subscriptions', 'Subscriptions'], ['/inbuilt-scraping', 'Inbuilt scraping'], ['/campaigns', 'Campaigns'], ['/reposting', 'Reposting'],
  ['/assistant', 'Assistant'], ['/settings/system', 'System'], ['/settings/api', 'API access'], ['/settings/chats', 'Chat data'], ['/settings', 'Settings'],
  ['/admin/feature-flags', 'Feature flags'], ['/admin/support', 'Support'], ['/admin/users', 'Users'],
  ['/admin/tenants', 'Tenants'], ['/admin/ui-debug', 'UI Debug'], ['/reseller/branding', 'Branding'],
];
function titleFor(pathname) {
  if (pathname === '/' ) return 'Dashboard';
  const hit = TITLES.find(([p]) => pathname === p || pathname.startsWith(p));
  return hit ? hit[1] : PRODUCT_NAME;
}

function Brand({ compact = false, logoUrl, appName }) {
  return (
    <Link to="/dashboard" className="flex items-center gap-2.5 min-w-0 tap" aria-label={appName}>
      <img src={logoUrl || BRAND_LOGO} alt="" className="h-8 w-8 shrink-0 object-contain drop-shadow-[0_2px_8px_hsl(var(--primary)/.45)]" />
      {!compact && (
        <span className="font-heading font-bold text-[17px] tracking-tight text-foreground truncate">
          {appName}
        </span>
      )}
    </Link>
  );
}

function NavItem({ item, onClick, dense = false, pill = null }) {
  return (
    <NavLink
      to={item.path}
      onClick={onClick}
      end={item.path === '/dashboard' || item.path === '/settings'}
      className={({ isActive }) => cn(
        'group flex items-center gap-3 rounded-lg text-sm font-medium transition-colors tap',
        dense ? 'px-3 py-2' : 'px-3 py-2.5',
        isActive
          ? 'bg-primary/12 text-foreground ring-1 ring-primary/25'
          : 'text-muted-foreground hover:text-foreground hover:bg-secondary/70'
      )}
    >
      {({ isActive }) => (
        <>
          <item.icon className={cn('h-[18px] w-[18px] shrink-0 transition-colors', isActive ? 'text-primary' : 'text-muted-foreground group-hover:text-foreground')} />
          <span className="truncate">{item.label}</span>
          {/* r16p: a count pill (the Inbox's conversations with unread). Real text, so a screen reader reads "Inbox, 3 unread". */}
          {pill && (
            <span
              className="ml-auto shrink-0 rounded-full bg-primary px-1.5 py-0.5 text-[10px] font-semibold leading-none tnum text-primary-foreground"
              data-testid={`nav-pill-${item.path.replace(/\//g, '')}`}
            >
              {pill}<span className="sr-only"> unread</span>
            </span>
          )}
          {/* Not being worked on for the next two releases. Real text, not a decoration, so a
              screen reader reads "Campaigns, coming soon". The link still works: the flag sets
              expectations, it does not lock a door. */}
          {item.soon && (
            <span
              className="ml-auto shrink-0 rounded-full border border-border/70 px-1.5 py-0.5 text-[10px] font-medium leading-none text-muted-foreground/75"
              title="Not being worked on yet - planned after the next two releases"
            >
              coming soon
            </span>
          )}
          {isActive && !pill && (
            <span
              className={cn('h-1.5 w-1.5 shrink-0 rounded-full bg-primary', !item.soon && 'ml-auto')}
              aria-hidden
            />
          )}
        </>
      )}
    </NavLink>
  );
}

function NavGroups({ isAdmin, isSuperAdmin, flags, onNavigate, dense, chats }) {
  // An admin keeps the section even with the entitlement off, because in V3 - unlike V1 - support
  // happens inside this UI, and an operator has to be able to open the page they are being asked
  // about. It grants nothing: every server path still checks getEnableSmmFeature() itself, and the
  // settings page says plainly when the account's own entitlement is off.
  const visible = GROUPS.filter((g) => !g.requires || isAdmin || !!flags?.[g.requires]);
  const r16mOn = r16mUiEnabled();
  const r16o3On = r16o3UiEnabled();
  // r16p items only once the chat routes answered (useChatsSummary.available): OFF, or a server before r16p = exactly r16n
  const r16pOn = !!(chats && chats.available);
  const pills = { chats: chats && chats.available ? navPillText(chats.summary) : null };
  return (
    <nav className="flex flex-col gap-5" aria-label="Primary">
      {visible.map((g) => (
        <div key={g.label}>
          <p className="px-3 mb-1.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground/70">{g.label}</p>
          <div className="flex flex-col gap-0.5">
            {g.items.filter((it) => (!it.r16m || r16mOn) && (!it.r16o3 || r16o3On) && (!it.r16p || r16pOn)).map((it) => (
              <NavItem key={it.path} item={it} onClick={onNavigate} dense={dense} pill={it.pill ? pills[it.pill] : null} />
            ))}
          </div>
        </div>
      ))}
      {isAdmin && (
        <div>
          <p className="px-3 mb-1.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-warning/80">Admin</p>
          <div className="flex flex-col gap-0.5">
            {ADMIN_ITEMS.filter((it) => !it.superAdmin || isSuperAdmin).map((it) => (
              <NavItem key={it.path} item={it} onClick={onNavigate} dense={dense} />
            ))}
          </div>
        </div>
      )}
    </nav>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { branding } = useBranding();
  const navigate = useNavigate();
  const location = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);

  const isFullWidth = FULL_WIDTH_PAGES.includes(location.pathname);
  const isAdmin = user?.role === 'owner' || user?.isAdmin;
  const isSuperAdmin = !!user?.isSuperAdmin;
  const flags = user?.flags;
  const appName = branding?.appName || PRODUCT_NAME;
  const logoUrl = branding?.logoUrl || BRAND_LOGO;
  const title = useMemo(() => titleFor(location.pathname), [location.pathname]);
  // r16p: the account's chat counts (one shared query, 60 s): the Inbox item, its pill and the dot on "More"
  const chats = useChatsSummary();
  const morePill = chats.available ? navPillText(chats.summary) : null;
  // r16sd Settings Doctor (lib/doctorSwitch.js parts accountBanner / credits): the account banners and the credit balances from
  // /uiapi/me. A server without the doctor sends neither: nothing is drawn (the r16pd shell).
  const doctor = useDoctorMe();
  const showAccountBanner = doctor.available && doctorUiOn('accountBanner');
  const showCredits = doctor.available && doctorUiOn('credits') && !!doctor.credits;

  useEffect(() => { setMoreOpen(false); }, [location.pathname]);

  const handleLogout = () => { logout(); navigate('/login'); };

  const bottomItems = GROUPS
    .filter((g) => !g.requires || isAdmin || !!flags?.[g.requires])
    .flatMap((g) => g.items)
    .filter((it) => BOTTOM_TABS.includes(it.path));
  const moreActive = !BOTTOM_TABS.some((p) => location.pathname.startsWith(p));

  return (
    <div className="min-h-dvh bg-background text-foreground">
      <AuroraSky />
      {/* ---------------------------------------------------------------- desktop sidebar */}
      <aside className="hidden lg:flex fixed inset-y-0 left-0 z-40 w-sidebar flex-col border-r border-border bg-card/70 backdrop-blur">
        <div className="h-topbar flex items-center px-5 border-b border-border/70">
          <Brand logoUrl={logoUrl} appName={appName} />
        </div>
        <div className="flex-1 overflow-y-auto px-3 py-4">
          <NavGroups isAdmin={isAdmin} isSuperAdmin={isSuperAdmin} flags={flags} chats={chats} />
        </div>
        <div className="border-t border-border/70 p-3">
          <div className="flex items-center gap-3 rounded-lg px-2 py-2">
            <div className="h-9 w-9 shrink-0 rounded-full bg-gradient-to-br from-primary to-accent text-white flex items-center justify-center font-semibold">
              {(user?.firstName || user?.email || '?')[0]?.toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium truncate">{user?.firstName ? `${user.firstName} ${user.lastName || ''}`.trim() : user?.email}</p>
              <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
            </div>
            <Button variant="ghost" size="icon" onClick={handleLogout} title="Log out" aria-label="Log out" className="text-muted-foreground hover:text-foreground">
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
          <p className="mt-1 px-2 text-[11px] text-muted-foreground/60">{appName} · v{PRODUCT_VERSION}</p>
        </div>
      </aside>

      {/* ---------------------------------------------------------------- main column */}
      <div className="relative z-[1] lg:pl-sidebar flex min-h-dvh flex-col min-w-0">
        <header className="sticky top-0 z-30 glass border-b border-border/70 safe-top">
          <div className="h-topbar flex items-center justify-between gap-3 px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-3 min-w-0 lg:hidden">
              <Brand logoUrl={logoUrl} appName={appName} />
            </div>
            <div className="hidden lg:flex items-center gap-2 min-w-0 text-sm text-muted-foreground">
              <span className="truncate">{appName}</span>
              <ChevronRight className="h-3.5 w-3.5 opacity-60" />
              <span className="text-foreground font-medium truncate">{title}</span>
            </div>
            <div className="flex items-center gap-1 sm:gap-2">
              {showCredits && <CreditsChip credits={doctor.credits} inUse={doctor.creditsInUse} />}
              <span className="hidden md:inline text-xs text-muted-foreground max-w-[16rem] truncate lg:hidden">{user?.email}</span>
              <Button variant="ghost" size="sm" onClick={handleLogout} className="text-muted-foreground hover:text-foreground tap lg:hidden" aria-label="Log out">
                <LogOut className="h-4 w-4" />
                <span className="hidden sm:inline">Log out</span>
              </Button>
              <Button variant="ghost" size="icon" className="lg:hidden tap" onClick={() => setMoreOpen(true)} aria-label="Open menu">
                <Menu className="h-5 w-5" />
              </Button>
            </div>
          </div>
        </header>

        <main
          key={location.pathname}
          className={cn(
            'flex-1 min-w-0 page-enter',
            isFullWidth ? 'p-0' : 'px-4 sm:px-6 lg:px-8 py-5 sm:py-6 mx-auto w-full max-w-[1440px]',
            'pb-[calc(var(--bottombar-h)+var(--safe-bottom)+1.25rem)] lg:pb-8'
          )}
        >
          {showAccountBanner && !isFullWidth && <AccountBanner holds={doctor.holds} />}
          {children}
        </main>
      </div>

      {/* ---------------------------------------------------------------- mobile bottom tabs */}
      <nav className="lg:hidden fixed bottom-0 inset-x-0 z-40 glass border-t border-border/70 safe-bottom" aria-label="Primary (mobile)">
        <div className="grid grid-cols-5 h-bottombar">
          {bottomItems.map((it) => (
            <NavLink
              key={it.path}
              to={it.path}
              className={({ isActive }) => cn(
                'flex flex-col items-center justify-center gap-1 text-[11px] font-medium transition-colors',
                isActive ? 'text-primary' : 'text-muted-foreground hover:text-foreground'
              )}
            >
              {({ isActive }) => (
                <>
                  <span className={cn('inline-flex h-7 w-12 items-center justify-center rounded-full transition-colors', isActive && 'bg-primary/15')}>
                    <it.icon className="h-5 w-5" />
                  </span>
                  {it.label}
                </>
              )}
            </NavLink>
          ))}
          <button
            type="button"
            onClick={() => setMoreOpen(true)}
            className={cn('flex flex-col items-center justify-center gap-1 text-[11px] font-medium transition-colors',
              moreActive ? 'text-primary' : 'text-muted-foreground hover:text-foreground')}
            aria-label="More"
          >
            <span className={cn('relative inline-flex h-7 w-12 items-center justify-center rounded-full', moreActive && 'bg-primary/15')}>
              <MoreHorizontal className="h-5 w-5" />
              {/* r16p: the Inbox lives in More on phones (O13); a dot says it has unread */}
              {morePill && (
                <span className="absolute right-2 top-0.5 h-2 w-2 rounded-full bg-primary ring-2 ring-background" aria-hidden data-testid="more-unread-dot" />
              )}
            </span>
            More
          </button>
        </div>
      </nav>

      {/* ---------------------------------------------------------------- drawer (< lg) */}
      <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
        <SheetContent side="left" className="w-[86vw] max-w-sm p-0 flex flex-col">
          <SheetHeader className="px-5 h-topbar flex flex-row items-center border-b border-border/70 space-y-0">
            <SheetTitle className="sr-only">Navigation</SheetTitle>
            <Brand logoUrl={logoUrl} appName={appName} />
          </SheetHeader>
          <div className="flex-1 overflow-y-auto px-3 py-4">
            <NavGroups isAdmin={isAdmin} isSuperAdmin={isSuperAdmin} flags={flags} chats={chats} onNavigate={() => setMoreOpen(false)} />
          </div>
          <div className="border-t border-border/70 p-4 safe-bottom">
            <p className="text-sm font-medium truncate">{user?.email}</p>
            <Button variant="outline" size="sm" onClick={handleLogout} className="mt-3 w-full tap">
              <LogOut className="h-4 w-4" /> Log out
            </Button>
            <p className="mt-3 text-[11px] text-muted-foreground/60">{appName} · v{PRODUCT_VERSION}</p>
          </div>
        </SheetContent>
      </Sheet>

      {/* Pinned to the bottom-right of every page. Renders nothing unless the assistant flag
          is on, and yields the corner to a page that claims it - see lib/fabStack.js. */}
      <AssistantBubble />
    </div>
  );
}
